import moment from 'moment';
import { Box, TableCell, TableRow, Tooltip, Typography, IconButton } from '@mui/material';
import { useEffect, useState } from 'react';

import TableContainer from '../../components/Table/TableContainer';
import { VENDOR_DOCUMENT_TABLE_LABELS } from '../../constants/tableLabels';
import { handleSortTableData } from '../../helpers/handleSortTableData';
import { ActiveSortField, VendorDocumentData } from '../../types';
import { VendorDocumentTableItem } from './types';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DeleteIcon from '@mui/icons-material/Delete';
import theme from '../../theme';
import ViewCommonButton from '../../components/buttons/ViewCommonButton';
import VendorDocumentUploadPopup from '../../components/VendorDocumentUploadPopup';
import ConfirmationPopup from '../../components/ConfirmationPopup';
import useSnackBar from '../../hooks/useSnackBar';
import { useHasPermission } from '../../hooks/useHasPermission';
import usePagination from '../../hooks/usePagination';
import {
  getVendorDocuments,
  uploadVendorDocuments,
  deleteVendorDocument,
  downloadVendorDocument,
} from '../../api/vendorApi/vendorAccountsApi';
import NoDataText from '../../components/NoDataText';
import InactiveUserName from '../../components/InactiveUserName';

interface VendorDocumentTabProps {
  participationId: number;
}

const truncateFileName = (name: string, maxLength = 40) => {
  if (!name) return '';
  return name.length > maxLength ? `${name.slice(0, maxLength)}...` : name;
};

const formatFileSize = (bytes: number) => {
  if (!bytes && bytes !== 0) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const VendorDocumentTab = ({ participationId }: VendorDocumentTabProps) => {
  const { setAlert } = useSnackBar();
  const canCreate = useHasPermission('Vendors', 'Create');
  const canDelete = useHasPermission('Vendors', 'Delete');
  const { pageNumber, rowsPerPage, setTotalCount } = usePagination();

  const [activeSortField, setActiveSortField] = useState<ActiveSortField | null>(null);
  const [isUploadPopupOpen, setIsUploadPopupOpen] = useState(false);
  const [documents, setDocuments] = useState<VendorDocumentData[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<VendorDocumentTableItem | null>(null);

  const fetchDocuments = () => {
    getVendorDocuments(participationId, { page: pageNumber, size: rowsPerPage, count: pageNumber === 1 })
      .then((response) => {
        setDocuments(response.data.resultSet);
        if (pageNumber === 1) {
          setTotalCount?.(response.data.totalCount ?? 0);
        }
      })
      .catch((error) => {
        setAlert({
          message: error.response?.data?.message || 'Failed to load documents',
          type: 'error',
        });
      });
  };

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participationId, pageNumber, rowsPerPage]);

  const formattedTableData: VendorDocumentTableItem[] = documents.map((document) => ({
    id: document.id,
    fileName: document.fileName,
    fileSize: formatFileSize(document.fileSize),
    uploadedBy: document.uploadedBy,
    uploadedByIsDeactivated: document.uploadedByIsDeactivated,
    uploadDate: moment(document.uploadDate).fromNow(),
    uploadDateFull: moment(document.uploadDate).format('MM/DD/YYYY hh:mm A'),
  }));

  const sortableFields = {
    fileName: { order: '', fieldName: 'fileName' },
    uploadDate: { order: '', fieldName: 'uploadDate' },
  };

  const handleUploadDocument = async (files: File[]) => {
    return uploadVendorDocuments(participationId, files);
  };

  const handleUploadSuccess = () => {
    fetchDocuments();
  };

  const handleDownloadFile = (tableItem: VendorDocumentTableItem) => {
    downloadVendorDocument(participationId, tableItem.id)
      .then((response) => {
        window.open(response.data.downloadUrl, '_blank');
      })
      .catch((error) => {
        setAlert({
          message: error.response?.data?.message || 'Failed to download file',
          type: 'error',
        });
      });
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    deleteVendorDocument(participationId, deleteTarget.id)
      .then(() => {
        setDeleteTarget(null);
        fetchDocuments();
        setAlert({ message: 'Document deleted successfully', type: 'success' });
      })
      .catch((error) => {
        setDeleteTarget(null);
        setAlert({
          message: error.response?.data?.message || 'Failed to delete document',
          type: 'error',
        });
      });
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <Typography
          sx={{
            fontSize: '20px',
            fontWeight: 600,
            color: theme.palette.common.white,
          }}
        >
          Documents
        </Typography>

        {canCreate && (
          <Box onClick={() => setIsUploadPopupOpen(true)}>
            <ViewCommonButton type="vendorDocumentUpload" />
          </Box>
        )}
      </Box>

      <VendorDocumentUploadPopup
        isOpen={isUploadPopupOpen}
        onClose={() => setIsUploadPopupOpen(false)}
        onSuccess={handleUploadSuccess}
        onUpload={handleUploadDocument}
      />

      <ConfirmationPopup
        open={!!deleteTarget}
        title="Delete Document"
        message={`Are you sure you want to delete "${deleteTarget?.fileName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />

      {formattedTableData.length === 0 ?
        <NoDataText marginTop='30px' />
        :
        <TableContainer
          headerLabels={VENDOR_DOCUMENT_TABLE_LABELS}
          sortableFields={sortableFields}
          activeSortField={activeSortField}
          setActiveSortField={setActiveSortField}
          handleSortTableData={handleSortTableData}
        >
          {formattedTableData.map((tableItem) => (
            <TableRow key={tableItem.id} sx={{ width: '100%', '&:hover': { cursor: 'default' } }}>
              <TableCell sx={{ maxWidth: '320px' }}>
                <Tooltip title={tableItem.fileName}>
                  <Typography
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {truncateFileName(tableItem.fileName, 40)}
                  </Typography>
                </Tooltip>
              </TableCell>

              <TableCell>{tableItem.fileSize}</TableCell>
              <TableCell>
                <InactiveUserName isInactive={tableItem.uploadedByIsDeactivated}>
                  <span>{tableItem.uploadedBy}</span>
                </InactiveUserName>
              </TableCell>

              <TableCell>
                <Tooltip title={tableItem.uploadDateFull}>
                  <Typography>{tableItem.uploadDate}</Typography>
                </Tooltip>
              </TableCell>

              <TableCell>
                <Box display="flex" gap="12px" alignItems="center">
                  <Tooltip title="Download File">
                    <IconButton
                      size="small"
                      onClick={() => handleDownloadFile(tableItem)}
                      sx={{ color: theme.palette.common.white }}
                    >
                      <FileDownloadIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>

                  {canDelete && (
                    <Tooltip title="Delete File">
                      <IconButton
                        size="small"
                        onClick={() => setDeleteTarget(tableItem)}
                        sx={{ color: theme.palette.common.white }}
                      >
                        <DeleteIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableContainer>}
    </>
  );
};

export default VendorDocumentTab;